import React, { useState, useEffect } from 'react';
import AdminLayout from '../layouts/AdminLayout';
import { offerSectionsAPI } from '../utils/api';
import './AdminOfferSectionPage.css';

const AdminOfferSectionPage = () => {
  const [offerSections, setOfferSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [formData, setFormData] = useState({
    image: null,
    alt_text: '',
    discount_percentage: '',
    title: '',
    description: '',
    button_text: 'Customize now',
    button_link: '/shop',
    is_active: true,
    sort_order: 0
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchOfferSections();
  }, []);

  const fetchOfferSections = async () => {
    try {
      const data = await offerSectionsAPI.getOfferSections();
      if (data.success) {
        setOfferSections(data.data.data || data.data);
      }
    } catch (error) {
      console.error('Error fetching offer sections:', error);
      setError('Failed to fetch offer sections');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      const file = files[0];
      setFormData(prev => ({ ...prev, [name]: file }));
      
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => setImagePreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        setImagePreview(null);
      }
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const resetForm = () => {
    setFormData({
      image: null,
      alt_text: '',
      discount_percentage: '',
      title: '',
      description: '',
      button_text: 'Customize now',
      button_link: '/shop',
      is_active: true,
      sort_order: 0
    });
    setImagePreview(null);
    setEditingSection(null);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const formDataToSend = new FormData();

      // Add all form fields to FormData
      Object.keys(formData).forEach(key => {
        // Always include boolean fields, even if false
        if (key === 'is_active') {
          formDataToSend.append(key, formData[key] ? '1' : '0');
        } else if (formData[key] !== null && formData[key] !== '') {
          // Handle other field types
          if (key === 'sort_order') {
            formDataToSend.append(key, formData[key].toString());
          } else {
            formDataToSend.append(key, formData[key]);
          }
        }
      });

      // Debug: Log FormData contents
      console.log('FormData being sent:');
      for (let [key, value] of formDataToSend.entries()) {
        console.log(key, ':', value);
      }

      const data = await offerSectionsAPI.updateOfferSection(editingSection.id, formDataToSend);

      if (data.success) {
        setSuccess('Offer section updated successfully!');
        fetchOfferSections();
        setShowModal(false);
        resetForm();
      } else {
        setError(data.message || 'An error occurred');
      }
    } catch (error) {
      console.error('Error saving offer section:', error);
      setError(error.message || 'An error occurred while saving');
    }
  };

  const handleEdit = (section) => {
    setEditingSection(section);
    setFormData({
      image: null,
      alt_text: section.alt_text || '',
      discount_percentage: section.discount_percentage || '',
      title: section.title || '',
      description: section.description || '',
      button_text: section.button_text || 'Customize now',
      button_link: section.button_link || '/shop',
      is_active: section.is_active,
      sort_order: section.sort_order || 0
    });
    setImagePreview(section.image_url || null);
    setShowModal(true);
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      const formData = new FormData();
      formData.append('is_active', !currentStatus ? '1' : '0');

      const data = await offerSectionsAPI.updateOfferSection(id, formData);

      if (data.success) {
        setSuccess('Offer section status updated!');
        fetchOfferSections();
      } else {
        setError(data.message || 'An error occurred');
      }
    } catch (error) {
      console.error('Error updating offer section:', error);
      setError(error.message || 'An error occurred while updating');
    }
  };


  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-offer-sections-page">
          <div className="loading">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-offer-sections-page">
      <div className="page-header">
        <h1>Offer Sections Management</h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="offer-sections-grid">
        {offerSections.length === 0 ? (
          <div className="no-data">
            <p>No offer sections found. Create your first one!</p>
          </div>
        ) : (
          offerSections.map((section, index) => (
            <div key={section.id} className="offer-section-card">
              <div className="card-header">
                <div className="section-info">
                  <h3>{section.title || 'Untitled Section'}</h3>
                  <span className={`status ${section.is_active ? 'active' : 'inactive'}`}>
                    {section.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="card-actions">
                  <button
                    className="btn btn-sm btn-warning"
                    onClick={() => handleToggleActive(section.id, section.is_active)}
                  >
                    {section.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleEdit(section)}
                  >
                    Edit
                  </button>
                </div>
              </div>
              
              <div className="card-content">
                {section.image_url && (
                  <div className="section-image">
                    <img src={section.image_url} alt={section.alt_text} />
                  </div>
                )}
                
                <div className="section-details">
                  <div className="detail-row">
                    <strong>Discount:</strong> {section.discount_percentage}
                  </div>
                  <div className="detail-row">
                    <strong>Title:</strong> {section.title}
                  </div>
                  <div className="detail-row">
                    <strong>Description:</strong> {section.description}
                  </div>
                  <div className="detail-row">
                    <strong>Button Text:</strong> {section.button_text}
                  </div>
                  <div className="detail-row">
                    <strong>Button Link:</strong> {section.button_link}
                  </div>
                  <div className="detail-row">
                    <strong>Sort Order:</strong> {section.sort_order}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Edit Offer Section</h2>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="image">Image</label>
                <input
                  type="file"
                  id="image"
                  name="image"
                  accept="image/*"
                  onChange={handleInputChange}
                />
                {imagePreview && (
                  <div className="image-preview">
                    <img src={imagePreview} alt="Preview" />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="alt_text">Alt Text</label>
                <input
                  type="text"
                  id="alt_text"
                  name="alt_text"
                  value={formData.alt_text}
                  onChange={handleInputChange}
                  placeholder="Image alt text"
                />
              </div>

              <div className="form-group">
                <label htmlFor="discount_percentage">Discount Percentage</label>
                <input
                  type="text"
                  id="discount_percentage"
                  name="discount_percentage"
                  value={formData.discount_percentage}
                  onChange={handleInputChange}
                  placeholder="e.g., 60% Off"
                />
              </div>

              <div className="form-group">
                <label htmlFor="title">Title</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Section title"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Section description"
                  rows="4"
                />
              </div>

              <div className="form-group">
                <label htmlFor="button_text">Button Text</label>
                <input
                  type="text"
                  id="button_text"
                  name="button_text"
                  value={formData.button_text}
                  onChange={handleInputChange}
                  placeholder="Button text"
                />
              </div>

              <div className="form-group">
                <label htmlFor="button_link">Button Link</label>
                <input
                  type="text"
                  id="button_link"
                  name="button_link"
                  value={formData.button_link}
                  onChange={handleInputChange}
                  placeholder="Button link URL"
                />
              </div>

              <div className="form-group">
                <label htmlFor="sort_order">Sort Order</label>
                <input
                  type="number"
                  id="sort_order"
                  name="sort_order"
                  value={formData.sort_order}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                  Active
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default AdminOfferSectionPage;
